import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  DynamicDropdown,
  DynamicMultiSelectDropdown,
  Form,
  FullScreenLoading,
  TextInput,
} from "../Components";
import { IAddOrEditSiteUserFormValues } from "../Interfaces/siteUserInterfaces";
import { getRoles } from "../Requests/roleAndPermissionsRequests";
import {
  addSiteUser,
  editSiteUser,
  getAssignableSahraUserGroups,
  getSiteUser,
} from "../Requests/siteUserRequests";
import { packetErrorHandler } from "../Utils/requestUtils";

interface IProps {
  userId?: number;
  onClose: () => void;
}

const emptyFormValues: IAddOrEditSiteUserFormValues = {
  firstName: "",
  lastName: "",
  username: "",
  password: "",
  confirmPassword: "",
  roleId: 0,
  sahraUserGroupIds: [],
};

const AddOrEditSiteUser = ({ userId, onClose }: IProps) => {
  const [formValues, setFormValues] =
    useState<IAddOrEditSiteUserFormValues>(emptyFormValues);
  const [loading, setLoading] = useState(!!userId);
  const [saving, setSaving] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const isEdit = !!userId;

  useEffect(() => {
    if (!userId) return;

    getSiteUser(userId)
      .then((result) =>
        setFormValues({
          ...emptyFormValues,
          id: result.id,
          username: result.username,
          firstName: result.firstName,
          lastName: result.lastName,
          roleId: result.roleId,
          sahraUserGroupIds: result.sahraUserGroupIds ?? [],
        }),
      )
      .catch((error) => {
        packetErrorHandler(error);
        onCloseRef.current();
      })
      .finally(() => setLoading(false));
  }, [userId]);

  const changeHandler = (name: string, value: any) => {
    const normalizedValue =
      name === "roleId"
        ? Number(value)
        : name === "sahraUserGroupIds"
          ? (value as Array<number | string>).map(Number)
          : value;

    setFormValues((current) => ({
      ...current,
      [name]: normalizedValue,
    }));
  };

  const checkDisabled = () => {
    if (
      !formValues.username ||
      !formValues.firstName ||
      !formValues.lastName ||
      !formValues.roleId
    )
      return true;

    if (isEdit) return false;

    return (
      !formValues.password ||
      !formValues.confirmPassword ||
      formValues.password !== formValues.confirmPassword
    );
  };

  const submitHandler = () => {
    setSaving(true);
    const request = isEdit
      ? editSiteUser({ ...formValues, id: userId })
      : addSiteUser(formValues);

    request
      .then((response) => {
        if (!response.data) {
          toast.error(response.msg);
          return;
        }

        toast.success(response.msg);
        onClose();
      })
      .catch(packetErrorHandler)
      .finally(() => setSaving(false));
  };

  if (loading) return <FullScreenLoading />;

  return (
    <div className="w-80 max-w-full m-auto">
      <div className="whitespace-nowrap max-w-min m-auto mb-5">
        {isEdit ? "ویرایش کاربر سایت" : "اضافه کردن کاربر سایت"}
      </div>
      <Form className="p-3 gap-3">
        <TextInput
          label="نام کاربری"
          name="username"
          onChange={changeHandler}
          value={formValues.username}
          autoComplete="off"
          extraLabel
        />
        <TextInput
          label="نام"
          name="firstName"
          onChange={changeHandler}
          value={formValues.firstName}
          extraLabel
        />
        <TextInput
          label="نام خانوادگی"
          name="lastName"
          onChange={changeHandler}
          value={formValues.lastName}
          extraLabel
        />
        <DynamicDropdown
          dropdown={{ name: "roleId", placeholder: "نقش", extraLabel: true }}
          getData={getRoles}
          onChange={changeHandler}
          value={formValues.roleId || null}
        />
        <DynamicMultiSelectDropdown
          dropdown={{
            name: "sahraUserGroupIds",
            placeholder: "دسته‌بندی اکانت",
            extraLabel: true,
          }}
          getData={getAssignableSahraUserGroups}
          onChange={changeHandler}
          value={formValues.sahraUserGroupIds}
          withTagBox
        />
        {!isEdit ? (
          <>
            <TextInput
              label="رمز عبور"
              name="password"
              onChange={changeHandler}
              value={formValues.password}
              type="password"
              autoComplete="new-password"
              extraLabel
            />
            <TextInput
              label="تایید رمز عبور"
              name="confirmPassword"
              onChange={changeHandler}
              value={formValues.confirmPassword}
              type="password"
              autoComplete="new-password"
              extraLabel
            />
          </>
        ) : (
          <></>
        )}
        <Button
          text={isEdit ? "ویرایش" : "تایید"}
          onClick={submitHandler}
          variant="contained"
          disabled={checkDisabled()}
          loading={saving}
        />
      </Form>
    </div>
  );
};

export default AddOrEditSiteUser;
